// Grammar Units 8 Data - Unit 8 (Infinitives Complete Course)
// Based on Ch08 不定詞完全攻略課程.jsx - Complete Infinitives Learning
// Organized for Supabase migration and systematic data management

export const grammarUnits8Data = [
  {
    id: 8,
    title: { traditional: '不定詞完全攻略課程', simplified: '不定词完全攻略课程', english: 'Complete Infinitives Course' },
    subtitle: { traditional: '掌握不定詞的四大功能', simplified: '掌握不定词的四大功能', english: 'Master the four main functions of infinitives' },
    description: { traditional: '系統性學習英文不定詞，包括名詞、形容詞、副詞功能和特殊用法。', simplified: '系统性学习英文不定词，包括名词、形容词、副词功能和特殊用法。', english: 'Systematic learning of English infinitives including noun, adjective, adverb functions and special usage.' },
    icon: '🎯',
    color: 'from-yellow-500 to-orange-600',
    status: 'notStarted',
    difficulty: 'advanced',
    estimatedHours: 4.5,
    content: {
      overview: {
        title: "不定詞的四大功能 (4 Main Functions of Infinitives)",
        core: "To + 動詞原形 (To + Base Verb)",
        functions: [
          {
            name: "名詞 Noun",
            icon: "🏠",
            description: "當主詞、受詞、補語",
            color: "bg-blue-100 border-blue-300 text-blue-800"
          },
          {
            name: "形容詞 Adjective", 
            icon: "🎨",
            description: "修飾名詞或形容詞",
            color: "bg-green-100 border-green-300 text-green-800"
          },
          {
            name: "副詞 Adverb",
            icon: "🎯", 
            description: "表目的、結果、程度",
            color: "bg-purple-100 border-purple-300 text-purple-800"
          },
          {
            name: "特殊 Special",
            icon: "⭐",
            description: "完成式、被動、慣用語",
            color: "bg-orange-100 border-orange-300 text-orange-800"
          }
        ]
      },
      nounFunction: {
        title: '名詞功能 (Noun Function)',
        icon: '🏠',
        color: 'blue',
        totalWays: 4,
        memory: {
          acronym: "SOCO",
          fullForm: "Subject-Object-Complement-Object(疑問詞)",
          story: "想像一個SOCO偵探🕵️‍♂️，他的工作就是找到不定詞在句子中扮演的角色！"
        },
        patterns: [
          {
            way: "作主詞 (Subject)",
            structure: "To + V + 單數動詞",
            when: "想強調這個動作本身",
            how: "把整個不定詞當作一個名詞來看待",
            example: "To learn English is important.",
            translation: "學英文很重要",
            tip: "記住：不定詞作主詞時，動詞要用單數！"
          },
          {
            way: "作受詞 (Object)", 
            structure: "S + Vt + to + V",
            when: "動詞後面需要受詞，且表達未來的動作",
            how: "某些動詞(want, decide, hope等)後面直接接不定詞",
            example: "I want to go home.",
            translation: "我想回家",
            tip: "常見動詞：want, hope, decide, plan, refuse, agree"
          },
          {
            way: "作補語 (Complement)",
            structure: "S + be + to + V",
            when: "說明主詞的身份或狀態",
            how: "be動詞後面用不定詞來補充說明主詞",
            example: "My goal is to pass the exam.",
            translation: "我的目標是通過考試",
            tip: "補語就是用來「補充說明」主詞的！"
          },
          {
            way: "疑問詞+不定詞 (Wh- + to + V)",
            structure: "疑問詞 + to + V",
            when: "不確定具體的動作，但知道要做某事",
            how: "what/how/where/when + to + V",
            example: "I don't know what to do.",
            translation: "我不知道該做什麼",
            tip: "這個結構可以代替名詞子句！"
          }
        ]
      },
      adjectiveFunction: {
        title: '形容詞功能 (Adjective Function)',
        icon: '🎨',
        color: 'green',
        totalWays: 4,
        memory: {
          acronym: "MINE",
          fullForm: "Modify-It句型-Noun-Emotion",
          story: "這些不定詞都是MINE我的！🎨 它們用來裝飾和修飾其他詞彙，就像畫家的顏料！"
        },
        patterns: [
          {
            way: "修飾形容詞 (Modify Adjectives)",
            structure: "形容詞 + to + V",
            when: "形容詞需要說明「對什麼而言」",
            how: "在形容詞後面直接加 to + V",
            example: "English is easy to learn.",
            translation: "英文很容易學",
            tip: "想想：對什麼而言是容易的？"
          },
          {
            way: "It句型 (It Pattern)",
            structure: "It + be + 形容詞 + (for sb) + to + V",
            when: "想表達「對某人來說做某事如何」",
            how: "用It當假主詞，真正主詞是後面的不定詞",
            example: "It's important for us to study hard.",
            translation: "對我們來說努力學習很重要",
            tip: "It只是個替身，真正的主詞在後面！"
          },
          {
            way: "修飾名詞 (Modify Nouns)",
            structure: "N + to + V",
            when: "名詞需要說明「做什麼用的」",
            how: "在名詞後面加 to + V 來說明用途或目的",
            example: "I have homework to do.",
            translation: "我有作業要做",
            tip: "可以改成關係子句：homework that I should do"
          },
          {
            way: "情緒形容詞 (Emotion Adjectives)",
            structure: "情緒形容詞 + to + V",
            when: "表達因為某事而產生的情緒",
            how: "happy, sad, excited, disappointed + to + V",
            example: "I'm happy to see you.",
            translation: "見到你我很高興",
            tip: "情緒的原因就在to + V裡面！"
          }
        ]
      },
      adverbFunction: {
        title: '副詞功能 (Adverb Function)', 
        icon: '🎯',
        color: 'purple',
        totalWays: 4,
        memory: {
          acronym: "PETS",
          fullForm: "Purpose-Enough-Too-So...as to",
          story: "養PETS寵物🐕需要目的！太多或足夠的愛，結果就是幸福！"
        },
        patterns: [
          {
            way: "表目的 (Purpose)",
            structure: "To + V = In order to + V",
            when: "想說明「為了什麼目的」做某事",
            how: "在句子前面或後面加 To + V",
            example: "I study hard to pass the exam.",
            translation: "我努力學習為了通過考試",
            tip: "可以用 In order to 來確認是否表目的"
          },
          {
            way: "enough...to (足夠...)",
            structure: "形容詞/副詞 + enough + to + V",
            when: "某程度足夠達到某結果",
            how: "enough放在形容詞/副詞後面",
            example: "He is old enough to drive.",
            translation: "他年紀夠大可以開車了",
            tip: "enough = 剛好足夠，是正面的結果"
          },
          {
            way: "too...to (太...)",
            structure: "too + 形容詞/副詞 + to + V",
            when: "程度太過以致於無法做某事",
            how: "too放在形容詞/副詞前面",
            example: "The box is too heavy to carry.",
            translation: "這箱子太重搬不動",
            tip: "too = 超過了，是負面的結果"
          },
          {
            way: "so...as to (如此...以致)",
            structure: "so + 形容詞/副詞 + as to + V",
            when: "程度很高導致某種結果",
            how: "so...as to 表示意想不到的結果",
            example: "He spoke so loudly as to wake everyone up.",
            translation: "他說話太大聲把大家都吵醒了",
            tip: "通常是意外或極端的結果"
          }
        ]
      },
      specialUses: {
        title: '特殊用法 (Special Uses)',
        icon: '⭐',
        color: 'orange', 
        totalWays: 3,
        memory: {
          acronym: "CPI",
          fullForm: "Complete-Passive-Idiom",
          story: "特殊用法像CPI消費者物價指數📈，有完成的過去、被動的現在、和固定的慣用語！"
        },
        patterns: [
          {
            way: "完成式 (Perfect Form)",
            structure: "to + have + p.p.",
            when: "不定詞的動作發生在主要動詞之前",
            how: "用完成式表示時間的先後順序",
            example: "He claims to have finished the work.",
            translation: "他聲稱已經完成了工作",
            tip: "to have done = 早就做完了"
          },
          {
            way: "被動語態 (Passive Voice)",
            structure: "to + be + p.p.",
            when: "不定詞的動作是被動的",
            how: "用 to be + 過去分詞",
            example: "The letter needs to be sent today.",
            translation: "這封信今天需要被寄出",
            tip: "焦點在於「被怎麼樣」"
          },
          {
            way: "慣用語 (Idioms)",
            structure: "固定搭配",
            when: "一些固定的表達方式",
            how: "整個片語記憶",
            example: "Needless to say, he failed the test.",
            translation: "不用說，他考試失敗了",
            tip: "這些要整個背起來！"
          }
        ]
      },
      memoryCards: [
        {
          front: "不定詞的基本結構",
          back: "To + 動詞原形",
          example: "To study, To eat, To go",
          tip: "永遠記住：To + 原形動詞！"
        },
        {
          front: "名詞功能記憶法",
          back: "SOCO偵探🕵️‍♂️",
          example: "Subject-Object-Complement-Object(疑問詞)",
          tip: "SOCO偵探幫你找到不定詞在句子中的角色！"
        },
        {
          front: "形容詞功能記憶法", 
          back: "MINE畫家🎨",
          example: "Modify-It句型-Noun-Emotion",
          tip: "這些都是我的顏料，用來裝飾句子！"
        },
        {
          front: "副詞功能記憶法",
          back: "PETS寵物🐕",
          example: "Purpose-Enough-Too-So...as to",
          tip: "養寵物需要目的，要有足夠的愛！"
        },
        {
          front: "特殊用法記憶法",
          back: "CPI指數📈", 
          example: "Complete-Passive-Idiom",
          tip: "像經濟指數一樣，有過去完成、現在被動、固定慣用！"
        },
        {
          front: "否定不定詞",
          back: "not + to + V",
          example: "I decided not to go.",
          translation: "我決定不去",
          tip: "否定詞放在to前面！"
        }
      ],
      practiceQuestions: [
        {
          question: "Choose the correct infinitive function: 'To learn English is important.'",
          options: ["Noun (Subject)", "Adjective", "Adverb", "Special"],
          correct: 0,
          explanation: "The infinitive 'To learn English' is acting as the subject of the sentence."
        },
        {
          question: "Complete: 'I want ___ home.'",
          options: ["go", "to go", "going", "went"],
          correct: 1,
          explanation: "After 'want', use 'to + base verb' (infinitive as object)."
        },
        {
          question: "Which sentence shows infinitive as adjective?",
          options: ["I have homework to do.", "I want to study.", "To sleep is important.", "I study to pass."],
          correct: 0,
          explanation: "'To do' modifies the noun 'homework', showing its purpose."
        },
        {
          question: "Choose: 'He is old enough ___ drive.'",
          options: ["to", "for", "of", "with"],
          correct: 0,
          explanation: "Use 'to + base verb' after 'enough' to show result."
        },
        {
          question: "Complete: 'The letter needs ___ sent today.'",
          options: ["to be", "to have", "being", "having"],
          correct: 0,
          explanation: "Use 'to be + past participle' for passive infinitive."
        }
      ],
      memoryTips: {
        title: "記憶技巧 Memory Tips",
        tips: [
          {
            category: "基本結構",
            content: "To + 動詞原形，永遠記住這個公式！"
          },
          {
            category: "名詞功能",
            content: "SOCO偵探：Subject-Object-Complement-Object(疑問詞)"
          },
          {
            category: "形容詞功能",
            content: "MINE畫家：Modify-It句型-Noun-Emotion"
          },
          {
            category: "副詞功能",
            content: "PETS寵物：Purpose-Enough-Too-So...as to"
          },
          {
            category: "特殊用法",
            content: "CPI指數：Complete-Passive-Idiom"
          },
          {
            category: "否定形式",
            content: "not + to + V，否定詞放在to前面"
          }
        ]
      }
    }
  }
];

// Export individual units for specific access
export const getUnitById = (unitId) => {
  return grammarUnits8Data.find(unit => unit.id === unitId);
};

export const getUnitsByDifficulty = (difficulty) => {
  return grammarUnits8Data.filter(unit => unit.difficulty === difficulty);
};

export const getCompletedUnits = () => {
  return grammarUnits8Data.filter(unit => unit.status === 'completed');
};

// Default export
export default grammarUnits8Data;
